import { Pipe, PipeTransform } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { ActionDto, ChanceNodeActionDto, ChanceNodeDto, TriggerDto } from "@api";
import { concatLatestFrom } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { RootState } from "src/app/store";
import * as FromScenario from "src/app/store/scenario/selectors";

interface GetTreeParams {
  node: ChanceNodeDto | ChanceNodeActionDto[];
  chanceNodes: ChanceNodeDto[];
  actions: ActionDto[];
  trigger: TriggerDto;
  isFirst?: boolean;
  reason?: boolean;
  parentId?: string;
}

@Pipe({ name: "toTree" })
export class ToTreePipe implements PipeTransform {
  constructor(private store: Store<RootState>, private domSanitizer: DomSanitizer, private i18n: TranslateService) {}

  public transform(loading: boolean, scenario: ChanceNodeDto): Observable<SafeHtml> {
    if (!scenario) return of("");

    return this.store.select(FromScenario.getActions).pipe(
      concatLatestFrom(() => this.store.select(FromScenario.getTrigger(scenario.triggerId))),
      concatLatestFrom(() => this.store.select(FromScenario.getChanceNodes)),
      map(([[actions, trigger], chanceNodes]) =>
        this.domSanitizer.bypassSecurityTrustHtml(`
          <ul class="scenario__actions">
            ${
              scenario.active
                ? `<li class="scenario__actions__item --stop" onclick='window.postMessage(${JSON.stringify({
                    type: "DEACTIVE_SCENARIO",
                  })})'>
                    <img src="/assets/scenarios/stop.svg" alt="Stop symbol" />
                  </li>`
                : ""
            }
            ${
              !scenario.active
                ? `<li class="scenario__actions__item --play" onclick='window.postMessage(${JSON.stringify({
                    type: "REACTIVE_SCENARIO",
                  })})'>
                    <img src="/assets/scenarios/active.svg" alt="Play symbol" />
                  </li>`
                : ""
            }
            <li class="scenario__actions__item --run" onclick='window.postMessage(${JSON.stringify({ type: "RUN_SCENARIO" })})'>
              <img src="/assets/scenarios/run.svg" alt="Run symbol" />
            </li>
          </ul>
          <ul class="scenario__tree">
            <li>
              ${this.gettree({ node: scenario, trigger, actions, chanceNodes, isFirst: true })}
            </li>
          </ul>
        `),
      ),
    );
  }

  private gettree({ node, actions, chanceNodes, isFirst, reason, parentId, trigger }: GetTreeParams): string {
    if (node && Array.isArray(node) && node.length) {
      const addActionMessage = JSON.stringify({ type: "ADD_ACTION", reason, chanceNodeId: node[0].chanceNodeId });
      const editActionMessage = JSON.stringify({
        type: "EDIT_ACTION",
        chanceNodeActionId: node[0].id,
        actionId: node[0].actionId,
        chanceNodeId: node[0].chanceNodeId,
        reason,
      });
      const openMultiactionMessage = JSON.stringify({ type: "OPEN_MULTIACTION", chanceNodeId: node[0].chanceNodeId, reason });
      const onActionClick = node.length > 1 ? openMultiactionMessage : editActionMessage;
      const action = actions.find(a => a.id === node[0].actionId);
      const name = node.length > 1 ? this.i18n.instant("ACTIONS.ACTION_STACK") : this.i18n.instant("ACTIONS." + action.name);
      const description = node.length > 1 ? this.i18n.instant("ACTIONS.MULTIPLE_ACTIONS") : this.getActionDescription(node[0], action);

      return `
        ${node.length > 1 ? "<div class='scenario__tree__item --stack'></div>" : ""}
        <div class="scenario__tree__item --action">
          <div class="scenario__tree__item__type" onclick='window.postMessage(${onActionClick})'>
            <img class="scenario__tree__item__type__icon" src="assets/scenarios/action.svg" />
            ${node.length > 1 ? `<div class="scenario__tree__item__type__badge">${node.length}</div>` : ""}

            <span class="scenario__tree__item__type__text">${this.i18n.instant("SCENARIOS.ACTION")}</span>
          </div>
          <div class="scenario__tree__item__description" onclick='window.postMessage(${onActionClick})'>
            <span class="scenario__tree__item__description__title">${name}</span>
            <span class="scenario__tree__item__description__text">${description}</span>
          </div>
          <div class="scenario__tree__item__add" onclick='window.postMessage(${addActionMessage})'>+</div>
        </div>
      `;
    } else if (this.isChanceNode(node)) {
      const isInactive: boolean = this.isChanceNode(node) && !node.active;
      const editScenarioMessage = JSON.stringify({ type: "EDIT_SCENARIO", chanceNodeId: node.id });

      return `
      <div class="scenario__tree__item ${isInactive ? "--inactive" : ""}" onclick='window.postMessage(${editScenarioMessage})'>
        <div class="scenario__tree__item__type">
          <img class="scenario__tree__item__type__icon" src="assets/scenarios/${
            isFirst && trigger ? trigger.type.toLowerCase() : "chance"
          }.svg" />
          <span class="scenario__tree__item__type__text">
            ${
              isFirst && trigger
                ? this.i18n.instant(`SCENARIOS.TRIGGER.${trigger.type.toUpperCase()}`)
                : this.i18n.instant("SCENARIOS.CHANCE")
            }
          </span>
        </div>
        <div class="scenario__tree__item__description">
          <span class="scenario__tree__item__description__title">${node?.name}</span>
          <span class="scenario__tree__item__description__text">${node?.description || ""}</span>
        </div>
      </div>
      <ul>
      ${this.getNodeChilds(node as ChanceNodeDto, chanceNodes)
        .map((child, index) => {
          if (child) {
            return `<li>${this.gettree({
              node: child as any,
              trigger,
              actions,
              chanceNodes,
              reason: index === 0,
              parentId: node.id,
            })}</li>`;
          }

          const addScenarioMessage = JSON.stringify({ type: "ADD_SCENARIO", reason: index === 0, parentId: node.id });
          const addActionMessage = JSON.stringify({ type: "ADD_ACTION", reason: index === 0, chanceNodeId: node.id });

          return `<li>
                    <div class="scenario__tree__item --choose">
                      <div class="scenario__tree__item__description">
                        <span class="scenario__tree__item__description__title">
                          ${this.i18n.instant("SCENARIOS.CHOOSE_NEXT_STEP")}
                        </span>
                        <span class="scenario__tree__item__description__text">
                          <span onclick='window.postMessage(${addScenarioMessage})'>${this.i18n.instant("SCENARIOS.CREATE_CHANCE")}</span>
                          <span>${this.i18n.instant("SCENARIOS.SPACED_OR")}</span>
                          <span onclick='window.postMessage(${addActionMessage})'>${this.i18n.instant("SCENARIOS.SELECT_ACTION")}</span>
                        </span>
                      </div>
                    </div>
                  </li>`;
        })
        .join("")}
      </ul>
    `;
    }
  }

  private getNodeChilds(node: ChanceNodeDto, chanceNodes: ChanceNodeDto[]): (ChanceNodeDto | ChanceNodeActionDto[])[] {
    const response = [null, null];
    if (node.nextIfTrueId) {
      response[0] = chanceNodes.find(chanceNode => chanceNode.id === node.nextIfTrueId);
    } else if (node.actions && node.actions.some(nodeAction => nodeAction.reason)) {
      response[0] = node.actions.filter(nodeAction => nodeAction.reason);
    }

    if (node.nextIfFalseId) {
      response[1] = chanceNodes.find(chanceNode => chanceNode.id === node.nextIfFalseId);
    } else if (node.actions && node.actions.some(nodeAction => !nodeAction.reason)) {
      response[1] = node.actions.filter(nodeAction => !nodeAction.reason);
    }

    return response;
  }

  private isChanceNode(node: ChanceNodeDto | ChanceNodeActionDto[]): node is ChanceNodeDto {
    return !!(node as ChanceNodeDto)?.triggerId;
  }

  private getActionDescription(chanceNodeAction: ChanceNodeActionDto, action: ActionDto): string {
    switch (action.name) {
      case "UPDATE_ACTION_BUTTON":
        const value = (chanceNodeAction?.parameters as any)?.type;
        if (value) return this.i18n.instant(`ACTIONS.LABEL.UPDATE_ACTION_BUTTON.${value}`);
        break;

      case "SEND_WEBHOOK":
        return (chanceNodeAction?.parameters as any)?.httpMethod;

      default:
        return action.description || "";
    }
  }
}
